import { Box } from '@mui/material';
import { useState } from 'react';

import { AlertsLimitsApi } from 'Api/AlertsLimits/AlertsLimitsApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { Modal } from 'Components/Modal/Modal';
import { ICON_CLOSE } from 'Config/Icons';
import { EffectivenessLimit, EffectivenessLimitsSelectOptions, ModifyLimitRequest } from 'Models/Limits';

import styles from './ModifyEffectivenessLimitModal.module.css';

export interface ModifyEffectivenessLimitModalProps {
    hideModal: () => void;
    alertsLimitsApi: AlertsLimitsApi;
    onLimitModified: () => void;
    limit: EffectivenessLimit;
}

export const ModifyEffectivenessLimitModal = (props: ModifyEffectivenessLimitModalProps) => {
    const [isModifyingLimit, setIsModifyingLimit] = useState(false);
    const [effectiveness, setEffectiveness] = useState(props.limit.alert_limit);
    const [failureMessage, setFailureMessage] = useState<string>();
    const [successMessage, setSuccessMessage] = useState<string>();

    const modifyLimit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        setFailureMessage(undefined);
        setSuccessMessage(undefined);
        if (effectiveness) {
            try {
                setIsModifyingLimit(true);
                const modifyLimitRequest: ModifyLimitRequest = { alert_limit: effectiveness };
                await props.alertsLimitsApi.modifyLimit(props.limit.id, modifyLimitRequest);
                setSuccessMessage('Limit updated.');
                props.onLimitModified();
            } catch (error) {
                handleRequestError(error);
            } finally {
                setIsModifyingLimit(false);
            }
        } else {
            setFailureMessage('Please select an Effectiveness Limit');
        }
    };

    const handleSelectChange = (value: ChangeEventType, formFieldId: string): void => {
        setEffectiveness(value as number);
    };

    const handleRequestError = (error: Error): void => {
        setFailureMessage(error.message);
        setSuccessMessage(undefined);
    };

    return (
        <Modal open header={`Modify Limit: ${props.limit.title}`}>
            {successMessage && <Alert type="success">{successMessage}</Alert>}
            {failureMessage && <Alert type="error">{failureMessage}</Alert>}
            <Box component="form" noValidate onSubmit={modifyLimit}>
                <div className={styles.fieldContainer}>
                    <FormFieldSelect options={EffectivenessLimitsSelectOptions} handleChange={handleSelectChange} formFieldId="effectiveness" formFieldLabel="Effectiveness" tooltip="Every time the effectiveness level drops below the selected effectiveness limit, you will be notified by email." selectedOption={effectiveness} required />
                </div>
                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE} disabled={isModifyingLimit}>
                        Close
                    </Button>
                    <Button variant="submit" disabled={successMessage ? true : false} isLoading={isModifyingLimit} loadingText="Saving...">
                        Save
                    </Button>
                </div>
            </Box>
        </Modal>
    );
};
